import {
  CalendarOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  GiftOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  List,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
// The raffle is fully translated in all three languages, so its dates follow
// the selected language rather than the fallback one — see
// `translatedDayjsLocale`. `hu` is loaded by `i18n/config.ts`, `en` is built in;
// `sr` is only needed here, so it is loaded here.
import 'dayjs/locale/sr';
import { useTranslation } from 'react-i18next';
import RaffleControlPanel from '../components/RaffleControlPanel';
import RafflePrizeList, { type RafflePrize } from '../components/RafflePrizeList';
import RaffleSettings from '../components/RaffleSettings';
import api from '../config/api';
import {
  raffleDelete,
  raffleErrorKey,
  raffleGet,
  rafflePatch,
  rafflePost,
} from '../config/raffleApi';
import { closeRaffleSocket } from '../config/socket';
import { translatedDayjsLocale } from '../i18n/locale';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;

/**
 * Raffle admin (#92).
 *
 * Three screens behind one route, because they are three moments of the same
 * job: build the rounds beforehand (dashboard + editor), run one in front of
 * the room (control panel), and hand the technician the wall URLs (settings).
 * The control panel deliberately REPLACES the dashboard rather than opening in
 * a modal — during a draw it is the only thing on screen that should be
 * clickable.
 */

interface RaffleDraw {
  id: string;
  eventId: string;
  name: string;
  scheduledAt: string;
  status: 'READY' | 'OPEN' | 'CLOSED' | 'DRAWING' | 'COMPLETED';
  identityField: 'EMAIL' | 'PHONE';
  registrationToken: string;
  order: number;
  RafflePrize: RafflePrize[];
  _count: {
    RaffleEntry: number;
  };
}

interface RaffleEvent {
  id: string;
  title: string;
  slug: string;
  organizationId: string;
}

interface DrawFormValues {
  name: string;
  scheduledAt: dayjs.Dayjs;
  identityField: 'EMAIL' | 'PHONE';
}

const drawStatusConfig = {
  READY: { color: 'default', labelKey: 'raffle.status.ready' },
  OPEN: { color: 'green', labelKey: 'raffle.status.open' },
  CLOSED: { color: 'orange', labelKey: 'raffle.status.closed' },
  DRAWING: { color: 'processing', labelKey: 'raffle.status.drawing' },
  COMPLETED: { color: 'default', labelKey: 'raffle.status.completed' },
} as const;

const Raffle: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();

  const [events, setEvents] = useState<RaffleEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string | undefined>();
  const [draws, setDraws] = useState<RaffleDraw[]>([]);
  const [loading, setLoading] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editingDraw, setEditingDraw] = useState<RaffleDraw | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<DrawFormValues>();

  /** Non-null while the control panel is on screen instead of the dashboard. */
  const [controlDrawId, setControlDrawId] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (user?.organizationId) {
        params.organizationId = user.organizationId;
      }
      const response = await api.get('/events', { params });
      const list: RaffleEvent[] = response.data.data || [];
      setEvents(list);
      setSelectedEvent((current) => current ?? list[0]?.id);
    } catch (error) {
      message.error(t('raffle.errors.eventsFailed'));
    }
  }, [user?.organizationId, t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fetchDraws = useCallback(async () => {
    if (!selectedEvent) {
      setDraws([]);
      return;
    }
    setLoading(true);
    try {
      const data = await raffleGet<RaffleDraw[]>('/raffle/draws', {
        eventId: selectedEvent,
      });
      setDraws(data);
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    } finally {
      setLoading(false);
    }
  }, [selectedEvent, t]);

  useEffect(() => {
    fetchDraws();
  }, [fetchDraws]);

  // The socket is a module-level singleton shared by every raffle screen, so
  // it is closed when the feature is left rather than by any one panel — a
  // panel that closed it on unmount would cut the connection out from under a
  // sibling that is still mounted.
  useEffect(() => closeRaffleSocket, []);

  const handleCreate = () => {
    setEditingDraw(null);
    setModalVisible(true);
  };

  /**
   * Open the editor on a FRESH copy of the round.
   *
   * The dashboard row is a snapshot from the last list fetch, and the prize
   * list hanging off it is the part that goes stale fastest — a co-organizer
   * adding a prize, or a round that has since been drawn, would otherwise be
   * edited against a list that no longer matches the server.
   */
  const handleEdit = async (draw: RaffleDraw) => {
    setEditingDraw(draw);
    setModalVisible(true);
    try {
      const fresh = await raffleGet<RaffleDraw>(`/raffle/draws/${draw.id}`);
      setEditingDraw(fresh);
      // `initialValues` only seeds the first mount, so the freshly read fields
      // are pushed in explicitly. Safe here — unlike before the modal opened,
      // the form is mounted by the time this network call resolves.
      form.setFieldsValue({
        name: fresh.name,
        scheduledAt: dayjs(fresh.scheduledAt),
        identityField: fresh.identityField,
      });
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    }
  };

  const handleSubmit = async (values: DrawFormValues) => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        scheduledAt: values.scheduledAt.toISOString(),
        identityField: values.identityField,
      };
      if (editingDraw) {
        await rafflePatch(`/raffle/draws/${editingDraw.id}`, payload);
        message.success(t('raffle.messages.updated'));
      } else {
        await rafflePost('/raffle/draws', {
          ...payload,
          eventId: selectedEvent,
        });
        message.success(t('raffle.messages.created'));
      }
      setModalVisible(false);
      fetchDraws();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (drawId: string) => {
    try {
      await raffleDelete(`/raffle/draws/${drawId}`);
      message.success(t('raffle.messages.deleted'));
      fetchDraws();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    }
  };

  const handleDuplicate = async (draw: RaffleDraw) => {
    try {
      // The backend copies the prizes, leaves participants and winners behind
      // and mints a fresh registration token — so the old QR keeps pointing at
      // the old round. Nothing to reproduce here; just report what came back.
      const copy = await rafflePost<RaffleDraw>(`/raffle/draws/${draw.id}/duplicate`);
      message.success(t('raffle.messages.duplicated', { name: copy.name }));
      fetchDraws();
    } catch (error) {
      message.error(t(raffleErrorKey(error)));
    }
  };

  /**
   * Rounds grouped by the day they are scheduled for.
   *
   * Grouped on the client on purpose: "which day is this" depends on the
   * organizer's timezone, and the backend deliberately leaves the decision here
   * (see `findAllForEvent`). The list arrives already ordered by `scheduledAt`,
   * so insertion order is the day order and no second sort is needed.
   */
  const drawsByDay = useMemo(() => {
    const groups = new Map<string, RaffleDraw[]>();
    draws.forEach((draw) => {
      const day = dayjs(draw.scheduledAt).format('YYYY-MM-DD');
      const bucket = groups.get(day);
      if (bucket) {
        bucket.push(draw);
      } else {
        groups.set(day, [draw]);
      }
    });
    return Array.from(groups.entries());
  }, [draws]);

  const selectedEventData = events.find((event) => event.id === selectedEvent);

  if (controlDrawId && selectedEvent) {
    return (
      <RaffleControlPanel
        drawId={controlDrawId}
        onBack={() => {
          setControlDrawId(null);
          fetchDraws();
        }}
        onChanged={fetchDraws}
      />
    );
  }

  const renderDraw = (draw: RaffleDraw) => {
    const statusConfig = drawStatusConfig[draw.status];
    const awarded = draw.RafflePrize.filter((prize) => prize.status === 'AWARDED').length;

    return (
      <List.Item key={draw.id}>
        <Card
          size="small"
          style={{ width: '100%' }}
          // Both halves of the header live in `title`, not in `title` + `extra`.
          // antd's card head is a nowrap flex row and `.ant-card-extra` carries
          // `margin-inline-start: auto`, so below roughly 700px the buttons slide
          // straight over the round's name — measured at 606px, with the name
          // completely hidden behind the primary button. One wrapping flex row
          // drops the actions onto their own line instead. `whiteSpace: normal`
          // is needed too: the head title otherwise ellipsises its content.
          styles={{ header: { whiteSpace: 'normal' } }}
          title={
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                paddingBlock: 8,
              }}
            >
              <Space wrap>
                <Text strong>{draw.name}</Text>
                <Tag color={statusConfig.color}>{t(statusConfig.labelKey)}</Tag>
              </Space>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={() => setControlDrawId(draw.id)}
                >
                  {t('raffle.actions.controlPanel')}
                </Button>
                <Button icon={<EditOutlined />} onClick={() => handleEdit(draw)}>
                  {t('common.edit')}
                </Button>
                <Button icon={<CopyOutlined />} onClick={() => handleDuplicate(draw)}>
                  {t('raffle.actions.duplicate')}
                </Button>
                {/* Deleting takes the entries and the winner log with it — the
                  round's whole audit trail. Never on a single click. */}
                <Popconfirm
                  title={t('raffle.confirmDeleteTitle')}
                  description={t('raffle.confirmDeleteBody')}
                  onConfirm={() => handleDelete(draw.id)}
                  okText={t('common.yes')}
                  cancelText={t('common.no')}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    {t('common.delete')}
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          }
        >
          <Space size="large" wrap>
            <Text type="secondary">{dayjs(draw.scheduledAt).format('HH:mm')}</Text>
            <Space size={4}>
              <TeamOutlined />
              <Text>
                {/* Not named `count`: that key switches i18next into plural
                    resolution and would look for `participants_one`. */}
                {t('raffle.dashboard.participants', {
                  value: draw._count?.RaffleEntry ?? 0,
                })}
              </Text>
            </Space>
            <Space size={4}>
              <GiftOutlined />
              <Text>
                {t('raffle.dashboard.prizeProgress', {
                  awarded,
                  total: draw.RafflePrize.length,
                })}
              </Text>
            </Space>
            <Tag>
              {draw.identityField === 'EMAIL'
                ? t('raffle.identityField.email')
                : t('raffle.identityField.phone')}
            </Tag>
          </Space>
        </Card>
      </List.Item>
    );
  };

  const dashboardTab = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          disabled={!selectedEvent}
        >
          {t('raffle.actions.newDraw')}
        </Button>
      </div>

      {loading && <Spin />}

      {!loading && draws.length === 0 && <Empty description={t('raffle.dashboard.empty')} />}

      {drawsByDay.map(([day, dayDraws]) => (
        <div key={day}>
          <Divider orientation="left">
            <Space>
              <CalendarOutlined />
              {dayjs(day).locale(translatedDayjsLocale(i18n.language)).format('YYYY. MMMM D. dddd')}
            </Space>
          </Divider>
          <List dataSource={dayDraws} renderItem={renderDraw} split={false} />
        </div>
      ))}
    </Space>
  );

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            {t('raffle.title')}
          </Title>
          <Select
            style={{ width: 280 }}
            placeholder={t('common.event')}
            value={selectedEvent}
            onChange={setSelectedEvent}
            options={events.map((event) => ({
              value: event.id,
              label: event.title,
            }))}
          />
        </div>

        <Tabs
          items={[
            {
              key: 'draws',
              label: t('raffle.tabs.draws'),
              children: dashboardTab,
            },
            {
              key: 'settings',
              label: t('raffle.tabs.settings'),
              children: selectedEvent ? (
                <RaffleSettings
                  eventId={selectedEvent}
                  eventSlug={selectedEventData?.slug}
                  draws={draws}
                />
              ) : (
                <Empty description={t('raffle.dashboard.selectEvent')} />
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title={editingDraw ? t('raffle.editTitle') : t('raffle.newTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnHidden
        width={820}
      >
        {/* Seeded through `initialValues`, not `setFieldsValue`: with
            `destroyOnHidden` the form is unmounted between openings, and
            writing to an unmounted instance is what antd warns about. A new
            round opens on the next full hour, which is what an organizer
            scheduling one on the day almost always wants. */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            name: editingDraw?.name,
            scheduledAt: editingDraw
              ? dayjs(editingDraw.scheduledAt)
              : dayjs().add(1, 'hour').startOf('hour'),
            identityField: editingDraw?.identityField ?? 'EMAIL',
          }}
        >
          <Form.Item
            name="name"
            label={t('raffle.form.name')}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <Input placeholder={t('raffle.form.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="scheduledAt"
            label={t('raffle.form.scheduledAt')}
            rules={[{ required: true, message: t('common.requiredField') }]}
            tooltip={t('raffle.form.scheduledAtHint')}
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="identityField"
            label={t('raffle.form.identityField')}
            tooltip={t('raffle.form.identityFieldHint')}
            rules={[{ required: true, message: t('common.requiredField') }]}
          >
            <Select
              options={[
                { value: 'EMAIL', label: t('raffle.identityField.email') },
                { value: 'PHONE', label: t('raffle.identityField.phone') },
              ]}
            />
          </Form.Item>

          <Space>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              {t('common.save')}
            </Button>
            <Button onClick={() => setModalVisible(false)}>{t('common.cancel')}</Button>
          </Space>
        </Form>

        {/* Deliberately a SIBLING of the form, not a child: the prizes are
            their own resource with their own endpoints, and nesting their
            modal's form inside this one only muddles the labels. They appear
            once the round exists — on a new round the organizer saves first,
            reopens, and the list is there. */}
        {editingDraw && (
          <>
            <Divider orientation="left">{t('raffle.prizes.title')}</Divider>
            <RafflePrizeList
              drawId={editingDraw.id}
              prizes={editingDraw.RafflePrize}
              readOnly={editingDraw.status === 'COMPLETED'}
              onChanged={async () => {
                await fetchDraws();
                // `editingDraw` is a snapshot taken when the modal opened, so
                // it has to be re-pointed at the refreshed row or the list
                // would render the prizes as they were before the edit.
                try {
                  setEditingDraw(await raffleGet<RaffleDraw>(`/raffle/draws/${editingDraw.id}`));
                } catch (error) {
                  message.error(t(raffleErrorKey(error)));
                }
              }}
            />
          </>
        )}
      </Modal>
    </Card>
  );
};

export default Raffle;
